/**
 * NMS Cart Drawer Auto-Opener
 * Utility service for opening cart drawer after redirect from /cart page
 *
 * This is a utility (not a component) because:
 * - No DOM element to manage
 * - No refs or event binding needed
 * - Global service functionality
 *
 * Safety Features:
 * - Only runs when flag is present (after /cart redirect)
 * - Doesn't interfere with normal cart drawer operations
 * - Falls back to cart link click if direct method fails
 * - Respects browser privacy settings (sessionStorage)
 */
class NMSCartDrawerAutoOpen {
  constructor() {
    // Initialize global namespace
    window.neemans = window.neemans || {};
    window.neemans.cartRedirect = window.neemans.cartRedirect || {};

    // Store instance in global namespace
    window.neemans.cartRedirect.autoOpener = this;

    this.maxRetries = 20; // Try for up to 2 seconds
    this.retryCount = 0;
    this.#init();
  }

  #init() {
    this.#checkAndOpenDrawer();
  }

  #checkAndOpenDrawer() {
    // Check if we should open the cart drawer (after redirect from /cart)
    let shouldOpenCart = false;

    try {
      shouldOpenCart = sessionStorage.getItem('nms-open-cart-drawer') === 'true';
    } catch (error) {
      // SessionStorage not available (privacy mode, etc.)
      console.warn('[NMS Cart Redirect] Could not read cart drawer flag:', error);
      return;
    }

    if (shouldOpenCart) {
      // Remove the flag immediately to prevent multiple attempts
      try {
        sessionStorage.removeItem('nms-open-cart-drawer');
      } catch (error) {
        console.warn('[NMS Cart Redirect] Could not remove cart drawer flag:', error);
      }

      // Wait for cart drawer to be available and fully initialized
      this.#tryOpenCartDrawer();
    }
  }

  #tryOpenCartDrawer() {
    const cartDrawer = document.getElementById('cart-drawer');

    // Check if cart drawer exists and is a custom element
    if (cartDrawer && window.customElements.get('cart-drawer')) {
      // Try multiple methods to open the drawer

      // Method 1: Direct show() call
      if (typeof cartDrawer.show === 'function') {
        try {
          cartDrawer.show();
          this.#logSuccess('show()');
          return;
        } catch (error) {
          this.#logWarning('show()', error);
        }
      }

      // Method 2: Trigger click on cart link with aria-controls
      const cartLink = document.querySelector('[aria-controls="cart-drawer"]');
      if (cartLink) {
        try {
          cartLink.click();
          this.#logSuccess('cart link click');
          return;
        } catch (error) {
          this.#logWarning('cart link click', error);
        }
      }
    }

    // If cart drawer not ready, retry
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      setTimeout(() => this.#tryOpenCartDrawer(), 100);
    } else {
      this.#logError();
    }
  }

  // Logging helpers with NMS prefix
  #logSuccess(method) {
    console.log(`✅ [NMS Cart Redirect] Cart drawer opened via ${method}`);
  }

  #logWarning(method, error) {
    console.warn(`⚠️ [NMS Cart Redirect] Failed to open cart drawer via ${method}:`, error);
  }

  #logError() {
    console.error('❌ [NMS Cart Redirect] Failed to open cart drawer after maximum retries');
  }
}

// Initialize when DOM is ready
function initNMSCartRedirect() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new NMSCartDrawerAutoOpen();
    });
  } else {
    new NMSCartDrawerAutoOpen();
  }
}

// Auto-initialize
initNMSCartRedirect();
